//#region Imports
import { useEffect, useState } from 'react';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { Text, Platform, SafeAreaView, View, Image, StyleSheet, useWindowDimensions } from 'react-native';
import { useColorScheme } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import HomePageLeaves from '../screens/HomeShortcutsPage/HomePageLeaves';
import HomePageSurveys from '../screens/HomeShortcutsPage/HomePageSurveys';
import HomePageAssessment from '../screens/HomeShortcutsPage/HomePageAssessment';
import HomePageTimesheets from '../screens/HomeShortcutsPage/HomePageTimesheets';
import HomePageDashboard from '../screens/HomeShortcutsPage/HomePageDashboard';
import HomePageMyTrainings from '../screens/HomeShortcutsPage/HomePageMyTrainings';
import HomePageEvents from '../screens/HomeShortcutsPage/HomePageEvents';
import CustomLabel from '../components/CustomLabel/CustomLabel';
import { useNavigation, useNavigationState, useRoute } from '@react-navigation/native';
import { useAccount } from '../hooks/useAccount';
import { HRAdminCompanyContextProvider } from '../context/HRAdminCompanyContext';
import withRoleGuard from '../components/withRoleGuard';
import useRole from '../hooks/userRole';
import { TASKS, TASK_OPTIONS } from '../constants/Tasks';
//#endregion

const Tab = createMaterialTopTabNavigator();
const GuardedHomePageLeaves = withRoleGuard(HomePageLeaves, TASKS.ALLEMPLOYEETASKS);
const GuardedHomePageSurveys = withRoleGuard(HomePageSurveys, TASK_OPTIONS.SURVEYS, TASKS.ALLEMPLOYEETASKS);
const GuardedHomePageAssessment = withRoleGuard(HomePageAssessment, TASK_OPTIONS.ASSESSMENTS, TASKS.ALLEMPLOYEETASKS);
const GuardedHomePageTimesheets = withRoleGuard(HomePageTimesheets, TASK_OPTIONS.TIMESHEETHOME, TASKS.ALLEMPLOYEETASKS); 

const TopTabNavigation = ({ drawer, }) => {

    //#region Definitions
    const navigation = useNavigation();
    const { width, height } = useWindowDimensions();
    const { hasAccess, hasOptionAccess, isAdmin } = useRole();
    const { t } = useTranslation();
    const { colors } = useTheme()
    const { accountGet } = useAccount();
    //! Forcing Light Mode for now
    let colorScheme = useColorScheme();
    colorScheme = 'light';
    const [permissions, setPermissions] = useState({});
    const [customWidth, setCustomWidth] = useState(width);
    const [tabBarHidden, setTabBarHidden] = useState(false);
    const styles = makeStyles();
    //#endregion

    //#region RefreshWweb
    //* if refreshed, navigate to last visited page
    const checkIfRefreshed = async () => {
        if (Platform.OS !== 'web') return; // since on Android/iOS there is no localStorage
        try {
            let isRefresh = localStorage.getItem('isRefresh');
            if (isRefresh === "true") {
                let lastVisitedScreen = localStorage.getItem('lastVisitedScreen');
                // console.log(lastVisitedScreen)
                switch (lastVisitedScreen) {
                    case "HomePageLeaves":
                        navigation.navigate('home', { screen: 'Leaves' });
                        break;
                    case "HomePageSurveys":
                        navigation.navigate('home', { screen: 'Surveys' });
                        break;
                    case "HomePageAssessment":
                        navigation.navigate('home', { screen: 'Assessment' });
                        break;
                    case "HomePageDashboard":
                        navigation.navigate('home', { screen: 'Dashboard' });
                        break;
                    case "HomePageTimesheets":
                        navigation.navigate('home', { screen: 'Timesheets' });
                        break;
                    case "HomePageMyTrainings":
                        navigation.navigate('home', { screen: 'MyTrainings' });
                        break;
                    case "HomePageEvents":
                        navigation.navigate('home', { screen: 'Events' });
                        break;
                    //   default:
                    //     navigation.navigate(lastVisitedScreen);
                    //     break;
                };
            };
        } catch (error) {
            console.log(error);
        };
    };

    const accGet = async () => {
        await accountGet().catch(console.error);
    }

    //#endregion

    //#region UseEffects 
    useEffect(
        () => {
            // * No need to do anything here
        },
        [width, height]
    );

    useEffect(() => {
        accGet().then(() => { checkIfRefreshed(); })
    }, [])

    useEffect(() => {
        //* Set Drawer Navigation 
        AsyncStorage.setItem(
            "drawer",
            drawer)

    }, []);
    //#endregion
    // * if permissions is not loaded show loading screen 
    if (typeof permissions === 'object' && !Array.isArray(permissions) && permissions === null) {
        return (
            <SafeAreaView
                style={styles.main}>
                <CustomLabel
                    value={t("common:loading")}
                    type="ASSM"
                />
            </SafeAreaView>
        )
    }
    // * Load the top tab navigation for employee or HR administration which is selected from the Drawer Navigation 
    return (
        <SafeAreaView style={{ flex: 1 }}>
            <View style={{ flex: 1, flexDirection: 'column' }}>

                <HRAdminCompanyContextProvider>
                    <Tab.Navigator
                        backBehavior="history"
                        initialRouteName={'Dashboard'}
                        screenOptions={({ route }) => ({
                            headerShown: false,
                            lazy: true,
                            tabBarScrollEnabled: true,
                            animationEnabled: Platform.OS === 'web' ? false : true,
                            tabBarItemStyle: {
                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                            },
                            tabBarIndicatorStyle: {
                                backgroundColor: colors.top_tab_nav_underline,
                                height: 3,
                            },
                            tabBarStyle: [ {
                                borderTopWidth: 0,
                                borderTopColor: 'transparent',
                                elevation: 0,
                                shadowOpacity: 0,

                                backgroundColor: colorScheme === 'light' ? '#FFFFFF' : '#000000',
                                },Platform.OS !== 'web' && tabBarHidden ? { display: 'none' } : null
                            ], 
                            swipeEnabled: false, //! disable swiping for now, since only one tab
                        })}>

                        <Tab.Screen name="Dashboard"
                            component={HomePageDashboard}
                            lazy={false}
                            initialParams={{ setTabBarHidden }}
                            options={{
                                tabBarLabel: ({ focused, color }) => (
                                    <Text
                                        style={{
                                            fontWeight: focused ? 'bold' : '400',
                                            textAlign: 'center',
                                            width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                            fontSize: width < 800 ? 12 : 16,
                                            color: colors.top_tab_nav_text
                                        }}
                                    >{t('navigate:DB')}</Text>),
                                title: t('navigate:DB'),
                            }}
                        />
                        {/* //* For Backwards combatibility we are keeping similar userroles. Old SS was My Tasks was giving access in many self service base employee functions  */}

                        <Tab.Screen name="Leaves"
                            component={GuardedHomePageLeaves}
                            lazy={false}
                            options={{
                                tabBarLabel: ({ focused, color }) => (
                                    <Text
                                        style={{
                                            fontWeight: focused ? 'bold' : '400',
                                            textAlign: 'center',
                                            width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                            fontSize: width < 800 ? 12 : 16,
                                            color: colors.top_tab_nav_text
                                        }}
                                    >{t('navigate:LM')}</Text>),
                                title: t('navigate:LM'),
                            }}
                        />
                        {(hasOptionAccess(TASKS.ALLEMPLOYEETASKS, TASK_OPTIONS.SURVEYS) || isAdmin) &&
                            <Tab.Screen
                                name="Surveys"
                                component={GuardedHomePageSurveys}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:SM')}</Text>),
                                    title: t('navigate:SM'),
                                }}
                            />
                        }
                        {Platform.OS === 'web' && (hasOptionAccess(TASKS.ALLEMPLOYEETASKS, TASK_OPTIONS.ASSESSMENTS) || isAdmin) && <Tab.Screen
                            name="Assessment"
                            component={GuardedHomePageAssessment}
                            options={{
                                tabBarLabel: ({ focused, color }) => (
                                    <Text
                                        style={{
                                            fontWeight: focused ? 'bold' : '400',
                                            textAlign: 'center',
                                            width: Platform.OS === 'web' ? width * 0.12 : width < 700 ? width / 4 : width / 3,
                                            fontSize: width < 800 ? 12 : 16,
                                            color: colors.top_tab_nav_text
                                        }}
                                    >{t('navigate:AM')}</Text>),
                                title: t('navigate:AM'),
                            }}
                        />}
                        {Platform.OS === 'web' && (hasOptionAccess(TASKS.ALLEMPLOYEETASKS, TASK_OPTIONS.TIMESHEETHOME) || isAdmin) && <Tab.Screen
                            name="Timesheets"
                            component={GuardedHomePageTimesheets}
                            lazy={false}
                            options={{
                                tabBarLabel: ({ focused, color }) => (
                                    <Text
                                        style={{
                                            fontWeight: focused ? 'bold' : '400',
                                            textAlign: 'center',
                                            width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                            fontSize: width < 800 ? 12 : 16,
                                            color: colors.top_tab_nav_text
                                        }}
                                    >{t('navigate:TS')}</Text>),
                                title: t('navigate:TS'),
                            }}
                        />}

                        {Platform.OS === 'web' && (hasOptionAccess(TASKS.ALLEMPLOYEETASKS, TASK_OPTIONS.TRAININGHOME) || isAdmin) &&
                            <Tab.Screen name="MyTrainings"
                                component={HomePageMyTrainings}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:MyTRN')}</Text>),
                                    title: t('navigate:MyTRN'),
                                }}
                            />
                        }
                        {Platform.OS === 'web' && (hasOptionAccess(TASKS.ALLEMPLOYEETASKS, TASK_OPTIONS.MYEVENTS) || isAdmin) &&
                            <Tab.Screen name="Events"
                                component={HomePageEvents}
                                lazy={false}
                                options={{
                                    tabBarLabel: ({ focused, color }) => (
                                        <Text
                                            style={{
                                                fontWeight: focused ? 'bold' : '400',
                                                textAlign: 'center',
                                                width: Platform.OS === 'web' ? width * 0.1 : width < 700 ? width / 4 : width / 3,
                                                fontSize: width < 800 ? 12 : 16,
                                                color: colors.top_tab_nav_text
                                            }}
                                        >{t('navigate:EV')}</Text>),
                                    title: t('navigate:EV'),
                                }}
                            />
                        }
                    </Tab.Navigator >
                </HRAdminCompanyContextProvider>
            </View>

        </SafeAreaView>

    );
};

const makeStyles = () => StyleSheet.create({

    margin: {
        alignContent: 'right',
    }
})
export default TopTabNavigation;